import { Service } from '@toeverything/infra';
import type { Subscription } from 'rxjs';

import type { PresentationSettings } from '../types';
import type { PresentationStateMachine } from './presentation-state-machine';

/**
 * Storage keys for presentation settings (matches EditPropsStore)
 */
const STORAGE_KEYS = {
  fillScreen: 'blocksuite:presentation:fillScreen',
  blackBackground: 'blocksuite:presentation:blackBackground',
  hideToolbar: 'blocksuite:presentation:hideToolbar',
} as const;

/**
 * FollowerSettingsSyncService
 *
 * Single Responsibility: Synchronizing presentation settings from presenter
 * - Backs up original settings when following starts
 * - Applies presenter settings
 * - Restores original settings when following stops
 *
 * Depends on: PresentationStateMachine (Single Source of Truth)
 */
export class FollowerSettingsSyncService extends Service {
  private stateSubscription?: Subscription;
  private originalSettings: PresentationSettings | null = null;
  private wasFollowing = false;

  constructor(private readonly stateMachine: PresentationStateMachine) {
    super();
    this.setupSubscriptions();
  }

  private setupSubscriptions(): void {
    // Track state changes from state machine
    this.stateSubscription = this.stateMachine.state$.subscribe(state => {
      const isFollowing =
        state.type === 'following_free' || state.type === 'following_frames';

      if (isFollowing && !this.wasFollowing) {
        // Started following
        this.onStartFollowing();
      } else if (!isFollowing && this.wasFollowing) {
        // Stopped following
        this.onStopFollowing();
      }

      this.wasFollowing = isFollowing;

      // sl-ck8v: Settings are now synced via WorkspaceRealtimeService
      // Socket events will push presenter settings to sessionState$
    });
  }

  /**
   * Called when user starts following a presenter
   */
  private onStartFollowing(): void {
    // Backup current settings
    this.originalSettings = this.getCurrentSettings();
    console.log(
      '[FollowerSettingsSync] Backed up settings:',
      this.originalSettings
    );
  }

  /**
   * Called when user stops following
   */
  private onStopFollowing(): void {
    // Restore original settings
    if (this.originalSettings) {
      this.applySettings(this.originalSettings);
      console.log(
        '[FollowerSettingsSync] Restored settings:',
        this.originalSettings
      );
      this.originalSettings = null;
    }
  }

  /**
   * Get current settings from localStorage
   */
  private getCurrentSettings(): PresentationSettings {
    return {
      fillScreen: this.getStorageBoolean(STORAGE_KEYS.fillScreen, false),
      blackBackground: this.getStorageBoolean(
        STORAGE_KEYS.blackBackground,
        true
      ),
      hideToolbar: this.getStorageBoolean(STORAGE_KEYS.hideToolbar, false),
    };
  }

  /**
   * Apply settings to localStorage
   * This triggers EditPropsStore to pick up the changes
   */
  private applySettings(settings: PresentationSettings): void {
    this.setStorageBoolean(STORAGE_KEYS.fillScreen, settings.fillScreen);
    this.setStorageBoolean(
      STORAGE_KEYS.blackBackground,
      settings.blackBackground
    );
    this.setStorageBoolean(STORAGE_KEYS.hideToolbar, settings.hideToolbar);

    // Dispatch custom event to notify components in the same tab
    window.dispatchEvent(
      new CustomEvent('presentation-settings-changed', {
        detail: settings,
      })
    );
  }

  /**
   * Helper: Get boolean from localStorage
   */
  private getStorageBoolean(key: string, defaultValue: boolean): boolean {
    try {
      const value = localStorage.getItem(key);
      if (value === null) return defaultValue;
      return JSON.parse(value) === true;
    } catch {
      return defaultValue;
    }
  }

  /**
   * Helper: Set boolean in localStorage
   */
  private setStorageBoolean(key: string, value: boolean): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  override dispose(): void {
    // Restore settings if disposed while following
    if (this.wasFollowing && this.originalSettings) {
      this.applySettings(this.originalSettings);
    }

    this.stateSubscription?.unsubscribe();
    super.dispose();
  }
}
